import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Save, Trash2, Trophy } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const defaultSlot = {
  label: "1x",
  multiplier: 1,
  weight: 10,
  color: "from-sky-500 to-blue-700",
};

const defaultSettings = {
  enabled: true,
  min_bet: 1,
  max_bet: 20,
  rules_text: "Choose a wallet bet, drop the ball, and the payout is bet x landed multiplier. Wallet updates after the live result.",
  slots: [
    { id: "miss-left", label: "0x", multiplier: 0, weight: 26, color: "from-slate-500 to-slate-700" },
    { id: "one-left", label: "1x", multiplier: 1, weight: 13, color: "from-sky-500 to-blue-700" },
    { id: "two-left", label: "2x", multiplier: 2, weight: 8, color: "from-cyan-400 to-blue-600" },
    { id: "five-left", label: "5x", multiplier: 5, weight: 3, color: "from-emerald-400 to-teal-700" },
    { id: "ten-center", label: "10x", multiplier: 10, weight: 1, color: "from-amber-300 to-orange-600" },
    { id: "five-right", label: "5x", multiplier: 5, weight: 3, color: "from-emerald-400 to-teal-700" },
    { id: "two-right", label: "2x", multiplier: 2, weight: 8, color: "from-cyan-400 to-blue-600" },
    { id: "one-right", label: "1x", multiplier: 1, weight: 13, color: "from-sky-500 to-blue-700" },
    { id: "miss-right", label: "0x", multiplier: 0, weight: 25, color: "from-slate-500 to-slate-700" },
  ],
};

const inputClass = "w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-slate-100 outline-none transition focus:border-[#2F6FA5]";
const labelClass = "mb-2 block text-xs font-black uppercase tracking-[0.16em] text-[#8FC5F3]";

function clampNumber(value, fallback = 0) {
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
}

function statsFor(slots) {
  const totalWeight = slots.reduce((sum, slot) => sum + Math.max(0, clampNumber(slot.weight)), 0);
  const winWeight = slots.reduce((sum, slot) => (
    clampNumber(slot.multiplier) > 0 ? sum + Math.max(0, clampNumber(slot.weight)) : sum
  ), 0);
  const averageMultiplier = totalWeight > 0
    ? slots.reduce((sum, slot) => sum + (Math.max(0, clampNumber(slot.multiplier)) * Math.max(0, clampNumber(slot.weight))), 0) / totalWeight
    : 0;
  return {
    totalWeight,
    winRatio: totalWeight ? (winWeight / totalWeight) * 100 : 0,
    rtp: averageMultiplier * 100,
  };
}

function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative h-8 w-14 rounded-full transition-colors ${checked ? "bg-[#2F6FA5]" : "bg-white/10"}`}
      aria-label="Toggle Plinko"
    >
      <span className={`absolute top-1 h-6 w-6 rounded-full bg-white transition-transform ${checked ? "translate-x-7" : "translate-x-1"}`} />
    </button>
  );
}

function normalizeSlotsTo100(slots = []) {
  const total = slots.reduce((sum, slot) => sum + Math.max(0, clampNumber(slot.weight)), 0);
  if (!total) return slots;
  const weighted = slots.map((slot, index) => {
    const exact = (Math.max(0, clampNumber(slot.weight)) / total) * 100;
    return {
      slot,
      index,
      weight: Math.floor(exact),
      remainder: exact - Math.floor(exact),
    };
  });
  let used = weighted.reduce((sum, row) => sum + row.weight, 0);
  weighted
    .sort((a, b) => b.remainder - a.remainder)
    .forEach((row) => {
      if (used < 100) {
        row.weight += 1;
        used += 1;
      }
    });
  weighted.sort((a, b) => a.index - b.index);
  return weighted.map((row) => ({ ...row.slot, weight: row.weight }));
}

function rebalanceSlotsForEditedChance(slots = [], editedIndex, editedValue) {
  const target = Math.max(0, Math.min(100, Math.round(clampNumber(editedValue, 0))));
  const remaining = 100 - target;
  const otherSlots = slots.filter((_, index) => index !== editedIndex);
  const otherTotal = otherSlots.reduce((sum, slot) => sum + Math.max(0, clampNumber(slot.weight)), 0);
  const otherCount = Math.max(1, otherSlots.length);
  const rows = slots.map((slot, index) => {
    if (index === editedIndex) {
      return { slot, index, weight: target, remainder: 0 };
    }
    const baseChance = otherTotal > 0
      ? (Math.max(0, clampNumber(slot.weight)) / otherTotal) * remaining
      : remaining / otherCount;
    return {
      slot,
      index,
      weight: Math.floor(baseChance),
      remainder: baseChance - Math.floor(baseChance),
    };
  });
  let used = rows.reduce((sum, row) => sum + row.weight, 0);
  rows
    .filter((row) => row.index !== editedIndex)
    .sort((a, b) => b.remainder - a.remainder)
    .forEach((row) => {
      if (used < 100) {
        row.weight += 1;
        used += 1;
      }
    });
  rows.sort((a, b) => a.index - b.index);
  return rows.map((row) => ({ ...row.slot, weight: row.weight }));
}

export default function AdminPlinko() {
  const [settings, setSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const stats = useMemo(() => statsFor(settings.slots || []), [settings.slots]);

  const sync = useCallback((data) => {
    setSettings({
      ...defaultSettings,
      ...(data?.settings || {}),
      slots: data?.settings?.slots?.length ? data.settings.slots : defaultSettings.slots,
    });
  }, []);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/plinko");
    sync(data);
  }, [sync]);

  useEffect(() => {
    setLoading(true);
    load()
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [load]);

  const updateSetting = (key, value) => {
    setSettings((current) => ({ ...current, [key]: value }));
  };

  const updateSlot = (index, key, value) => {
    setSettings((current) => ({
      ...current,
      slots: current.slots.map((slot, slotIndex) => (
        slotIndex === index ? { ...slot, [key]: value } : slot
      )),
    }));
  };

  const updateSlotChance = (index, value) => {
    setSettings((current) => ({
      ...current,
      slots: rebalanceSlotsForEditedChance(current.slots || [], index, value),
    }));
  };

  const addSlot = () => {
    setSettings((current) => ({
      ...current,
      slots: [
        ...current.slots,
        { ...defaultSlot, id: `slot-${Date.now()}` },
      ],
    }));
  };

  const removeSlot = (index) => {
    setSettings((current) => ({
      ...current,
      slots: current.slots.filter((_, slotIndex) => slotIndex !== index),
    }));
  };

  const normalizeWeights = () => {
    setSettings((current) => {
      return {
        ...current,
        slots: normalizeSlotsTo100(current.slots || []),
      };
    });
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = {
        enabled: Boolean(settings.enabled),
        min_bet: clampNumber(settings.min_bet, 1),
        max_bet: clampNumber(settings.max_bet, 20),
        rules_text: String(settings.rules_text || "").slice(0, 500),
        slots: normalizeSlotsTo100(settings.slots || []).map((slot, index) => ({
          id: slot.id || `slot-${index}`,
          label: String(slot.label || "").trim() || "0x",
          multiplier: clampNumber(slot.multiplier, 0),
          weight: clampNumber(slot.weight, 0),
          color: String(slot.color || "").trim(),
        })),
      };
      const { data } = await api.put("/admin/plinko", payload);
      sync(data);
      toast.success("Plinko settings saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 text-slate-100 sm:px-6">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-[#8FC5F3]">
            <Trophy className="h-5 w-5" />
            <span className="text-xs font-black uppercase tracking-[0.18em]">Wallet game</span>
          </div>
          <h1 className="mt-2 font-display text-3xl font-black">Plinko settings</h1>
          <p className="mt-1 text-sm text-slate-400">Control player wallet bets, slot multipliers, and win ratio.</p>
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-3">
          <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
            <div className="text-xs text-slate-400">Win chance</div>
            <div className="text-xl font-black text-emerald-300">{stats.winRatio.toFixed(1)}%</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
            <div className="text-xs text-slate-400">RTP estimate</div>
            <div className="text-xl font-black text-amber-300">{stats.rtp.toFixed(1)}%</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
            <div className="text-xs text-slate-400">Chance total</div>
            <div className={`text-xl font-black ${Math.round(stats.totalWeight) === 100 ? "text-emerald-300" : "text-amber-300"}`}>
              {stats.totalWeight}%
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={save} className="space-y-5">
        <section className="rounded-3xl border border-white/10 bg-[#111113] p-5">
          <div className="grid gap-4 lg:grid-cols-[auto_1fr_1fr] lg:items-end">
            <div>
              <span className={labelClass}>Enabled</span>
              <Toggle checked={settings.enabled} onChange={(value) => updateSetting("enabled", value)} />
            </div>
            <label>
              <span className={labelClass}>Minimum bet</span>
              <input className={inputClass} type="number" step="0.01" min="0.01" value={settings.min_bet} onChange={(event) => updateSetting("min_bet", event.target.value)} />
            </label>
            <label>
              <span className={labelClass}>Maximum bet</span>
              <input className={inputClass} type="number" step="0.01" min="0.01" value={settings.max_bet} onChange={(event) => updateSetting("max_bet", event.target.value)} />
            </label>
          </div>
          <label className="mt-4 block">
            <span className={labelClass}>Player rules text</span>
            <textarea className={`${inputClass} min-h-[100px]`} value={settings.rules_text} onChange={(event) => updateSetting("rules_text", event.target.value)} />
          </label>
        </section>
        <section className="rounded-3xl border border-white/10 bg-[#111113] p-5">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-black">Slots / win chance</h2>
              <p className="text-sm text-slate-400">Edit the win chance percentage directly. When you increase or decrease one chance, the other slots adjust automatically so the total stays 100%. Multiplier payout = bet x multiplier.</p>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <button type="button" onClick={normalizeWeights} className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-4 py-2 text-sm font-black text-emerald-200">
                Normalize to 100
              </button>
              <button type="button" onClick={addSlot} className="inline-flex items-center gap-2 rounded-full border border-[#2F6FA5]/40 bg-[#2F6FA5]/15 px-4 py-2 text-sm font-black text-[#8FC5F3]">
                <Plus className="h-4 w-4" />
                Add slot
              </button>
            </div>
          </div>

          <div className="space-y-3">
            {settings.slots.map((slot, index) => (
              <div key={`${slot.id || slot.label}-${index}`} className="grid gap-3 rounded-2xl border border-white/10 bg-black/20 p-3 md:grid-cols-[1.2fr_1fr_1fr_1.6fr_auto] md:items-end">
                <label>
                  <span className={labelClass}>Label</span>
                  <input className={inputClass} value={slot.label} onChange={(event) => updateSlot(index, "label", event.target.value)} />
                </label>
                <label>
                  <span className={labelClass}>Multiplier</span>
                  <input className={inputClass} type="number" step="0.01" min="0" value={slot.multiplier} onChange={(event) => updateSlot(index, "multiplier", event.target.value)} />
                </label>
                <label>
                  <span className={labelClass}>Win chance %</span>
                  <input className={inputClass} type="number" step="1" min="0" max="100" value={slot.weight} onChange={(event) => updateSlotChance(index, event.target.value)} />
                </label>
                <label>
                  <span className={labelClass}>Color class</span>
                  <input className={inputClass} value={slot.color || ""} onChange={(event) => updateSlot(index, "color", event.target.value)} />
                </label>
                <button
                  type="button"
                  onClick={() => removeSlot(index)}
                  disabled={settings.slots.length <= 2}
                  className="inline-flex h-11 items-center justify-center rounded-xl border border-red-500/30 px-3 text-red-200 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </section>

        <div className="sticky bottom-4 z-10 flex justify-end">
          <button
            type="submit"
            disabled={saving || loading}
            className="inline-flex items-center gap-2 rounded-full bg-[#2F6FA5] px-6 py-3 font-black text-white shadow-lg shadow-black/30 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Save className="h-4 w-4" />
            {saving ? "Saving..." : "Save Plinko"}
          </button>
        </div>
      </form>
    </div>
  );
}
